import { CloseRoundLight, MenuLightCustom } from '@repo/go-icons';
import { Button } from '@repo/go-ui';
import clsx from 'clsx';
import { observer } from 'mobx-react-lite';
import { Fragment, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { useStore } from 'src/app/root-store';
import { useService } from 'src/packages/di';

import { getMenuItems } from './MenuItems';
import styles from './SettingsGrid.module.scss';
import { SettingsStore } from './SettingsStore';
import type { TSettingType } from './types';

export const SettingsGrid = observer(function SettingsGrid() {
  const [searchParams] = useSearchParams();
  const section = searchParams.get('section');
  const wellId = searchParams.get('wellId');
  const wellboreId = searchParams.get('wellboreId');
  const menuItems = getMenuItems();

  const [settingsStore] = useState(() => {
    if (section) {
      const item = menuItems.find((item) => item.type === section);
      if (item) {
        const store = new SettingsStore();
        store.createSettingsScreen(item.type as TSettingType, !!(wellId || wellboreId))(item.id, {
          wellId: wellId ? Number(wellId) : undefined,
          wellboreId: wellboreId ? Number(wellboreId) : undefined,
        });
        return store;
      }
    }

    return new SettingsStore(menuItems[0].id);
  });

  const navigate = useNavigate();
  const { sidebar } = useStore();

  const screenRendererService = useService('settingsScreenRenderer');

  const { t } = useTranslation();

  const { mainColumn, createSettingsScreen, effect } = settingsStore;

  useEffect(effect, [effect]);

  const handleNavigate = (path: string) => {
    navigate(path);
  };

  return (
    <section className={styles.wrapper}>
      <div className={styles.headerWrapper}>
        <div className={styles.headerSection}>
          <div className={styles.menuIconWrapper}>
            <button type="button" className={styles.menuButton} onClick={sidebar.open}>
              <MenuLightCustom className={styles.menuIcon} />
            </button>
          </div>
          <h1 className={styles.header}>{t('sidebar:Nav.settings')}</h1>
        </div>

        <Button
          onClick={() => handleNavigate('/')}
          variant="flat"
          icon={<CloseRoundLight className={styles.closeButton} />}
        />
      </div>
      <div className={styles.contentWrapper}>
        <ul className={styles.mainColumn}>
          {menuItems.map((item) => (
            <li key={item.id} className={styles.mainColumnItem}>
              <button
                type="button"
                onClick={() => {
                  navigate(`/settings?section=${item.type}`);
                  createSettingsScreen(item.type)(item.id);
                }}
                disabled={item.disabled}
                className={clsx(styles.mainColumnButton, item.id === mainColumn && styles.mainColumnButton__active)}
              >
                {item.Icon} {t(`settings:${item.type}`)}
              </button>
            </li>
          ))}
        </ul>
        <div className={styles.grid}>
          {settingsStore.stack.stackArray.map((stackItem) => {
            const renderScreen = screenRendererService.renderScreen(stackItem, { stack: settingsStore.stack });

            return <Fragment key={stackItem.$$instanceId}>{renderScreen({})}</Fragment>;
          })}
        </div>
      </div>
    </section>
  );
});
